<script lang="ts" setup>
  import { ref, onMounted, watch, inject, watchEffect, nextTick } from 'vue'
  import AppImage from '@/components/AppImage.vue'
  import AppLoading from '@/components/AppLoading.vue'
  import ToastService from '@/components/AppToast/toast'
  import httpApi from '@/http/api'
  import getDomains from '@/utils/getDomains'
  import type { Job, JobListPerDay } from '@/entity'
  import { formatDuration } from '@/utils/common'
  import VideoPlayer from '@/components/VideoPlayer/index.vue'
  import MessageService from '@/components/AppMessage/message'

  const props = defineProps<{
    modelValue: Map<string, { imageResize512Url: string; url: string }>
    topicId: string
    wantable: boolean
    checkedLength: number
  }>()

  const emit = defineEmits(['checked', 'clearCheckedItems'])
  const generatorResult = ref<HTMLDivElement | null>(null)
  const checkedMaxLength = inject('checkedMaxLength')
  // 获取用户的历史记录
  const pageSize = 20
  let page = 1
  const firstLoading = ref(true)
  const loading = ref(false)
  const hasMore = ref(true)
  const curTab = ref<'image' | 'video'>('image')
  const isEdu = getDomains().thirdLevelDomain === 'edu'
  let totalData: Job[] = []
  const sourceData = ref<JobListPerDay[]>([])
  const challengeId: any = inject('challengeId')

  // 检查是否需要继续加载数据（兜底处理：当页面无法滚动但还有更多数据时）
  const checkAndLoadMore = async () => {
    await nextTick()
    if (loading.value || !hasMore.value) return
    if (!generatorResult.value) return

    const { scrollHeight, clientHeight } = generatorResult.value
    // 如果内容高度小于等于可视高度，说明没有滚动条，需要继续加载
    if (scrollHeight < clientHeight) {
      await getUserRecords()
    }
  }

  let controller = new AbortController()
  const getUserRecords = async () => {
    try {
      if (loading.value || !hasMore.value) return
      loading.value = true
      controller.abort()
      controller = new AbortController()
      const res: any = await httpApi.getHistoryList(
        page,
        curTab.value,
        pageSize,
        controller,
        isEdu ? 'edu' : 'mainsite',
        props.wantable ? challengeId.value || props.topicId : '',
        curTab.value === 'image' ? true : undefined,
        true
      )
      if (!res) return
      const curPageData = res.data.dataList ?? []
      hasMore.value = res.data.hasMore
      if (page === 1) {
        totalData = curPageData
      } else {
        totalData = [...totalData, ...curPageData]
      }
      page = res.data.page + 1
      firstLoading.value = false
      const mapData = mapHistoryListData(totalData)
      sourceData.value = formatListData(mapData)
    } catch {
      // empty
    } finally {
      loading.value = false
      await checkAndLoadMore()
    }
  }

  // 将数据按天分组
  const mapHistoryListData = (datas: Job[]) => {
    const groupMap = new Map<string, Job[]>()
    datas.forEach(item => {
      const date = item.createTime.split(' ')[0]
      const existingGroup = groupMap.get(date)
      if (existingGroup) {
        existingGroup.push(item)
      } else {
        groupMap.set(date, [item])
      }
    })

    const result: JobListPerDay[] = Array.from(groupMap).map(([createTime, list]) => ({
      createTime,
      list
    }))
    return result
  }

  // 将图片和3D和视频数据统一化处理
  const formatListData = (list: JobListPerDay[]) => {
    return list.map(item => {
      return {
        ...item,
        list: item.list.map(job => {
          return { ...job, datas: job.images || job.model3ds || (job.video ? [job.video] : []) }
        })
      }
    })
  }

  const handleScroll = () => {
    if (loading.value) return
    if (!hasMore.value) return
    const boxHeight = generatorResult.value?.clientHeight || 0
    const scrollHeight = generatorResult.value?.scrollHeight || 0
    const scrollTop = generatorResult.value?.scrollTop || 0
    if (scrollHeight - (scrollTop + boxHeight) < 200) {
      getUserRecords()
    }
  }

  const checkedList = ref(new Map<string, boolean>())
  const checkboxClickHandler = (item: any, uniqueTopicId: string) => {
    let checked = true
    if (!checkedList.value.has(item.url)) {
      if (curTab.value === 'video' && checkedList.value.size > 1) {
        ToastService({
          type: 'warning',
          message: `最多支持发布1个视频`
        })
        return false
      }

      if (props.checkedLength === checkedMaxLength) {
        ToastService({
          type: 'warning',
          message: `最多支持发布${checkedMaxLength}张图片`
        })
        return false
      }
      // todo 可能回影响相关逻辑
      if (props.wantable && checked) {
        const count = Array.from(props.modelValue.entries()).filter(([_, item]: any) => {
          return (
            props.topicId === item.uniqueTopicId ||
            item.wantableTopics.map((item: any) => item.uniqueTopicId).includes(props.topicId)
          )
        }).length
        if (count > 0) {
          ToastService({
            message: '「想要」挑战只支持发布1张图片',
            backgroundColor: '#707070',
            type: 'warning'
          })
          return
        }
      }
      checkedList.value.set(item.url, true)
    } else {
      checked = false
      checkedList.value.delete(item.url)
    }
    item.uniqueTopicId = uniqueTopicId || ''
    emit('checked', { item, checked })
  }

  const refreshData = () => {
    page = 1
    loading.value = false
    hasMore.value = true
    checkedList.value.clear()
    sourceData.value = []
    emit('clearCheckedItems')
    getUserRecords()
  }

  // topicId变更时，只能重刷数据，不能清空选中的图片数据
  watch(
    () => props.topicId,
    () => {
      page = 1
      loading.value = false
      hasMore.value = true
      sourceData.value = []
      getUserRecords()
    }
  )

  watch(
    () => curTab.value,
    () => {
      refreshData()
    }
  )

  watch(
    () => Array.from(props.modelValue.entries()),
    () => {
      initChecked()
    }
  )
  const initChecked = () => {
    checkedList.value.clear()
    Array.from(props.modelValue.entries()).forEach(([key, item]) => {
      checkedList.value.set(key, true)
    })
  }

  onMounted(() => {
    initChecked()
    getUserRecords()
  })

  watchEffect(() => {
    if (generatorResult.value) {
      generatorResult.value.addEventListener('scroll', handleScroll)
    }
  })

  const handleTabChange = (tabType: 'image' | 'video') => {
    if (tabType === curTab.value) return
    if (checkedList.value.size > 0) {
      MessageService({
        type: 'error',
        title: '放弃所有已选内容',
        message: '你正在尝试切换模式，这将清除所有已选内容',
        timer: 0,
        config: {
          showClose: false,
          showIcon: false,
          showCancel: true,
          showConfirm: true
        },
        onConfirm() {
          curTab.value = tabType
        }
      })
    } else {
      curTab.value = tabType
    }
  }
</script>
<template>
  <div class="history-container">
    <div class="tab-container">
      <div class="tab" :class="{ active: curTab === 'image' }" @click="handleTabChange('image')">
        图片
      </div>
      <div class="tab" :class="{ active: curTab === 'video' }" @click="handleTabChange('video')">
        视频
      </div>
    </div>
    <div class="list-container">
      <div class="loading-wraper-one" v-if="firstLoading">
        <AppLoading />
      </div>
      <div class="list-content" ref="generatorResult" v-if="sourceData.length > 0">
        <template v-for="data in sourceData" :key="data.createTime">
          <div class="day-item">
            <div class="date">{{ data.createTime }}</div>
            <div class="item-container">
              <template v-for="job in data.list" :key="job.id">
                <div v-for="item in job.datas" :key="item?.itemId" class="item">
                  <a
                    @click.stop="
                      checkboxClickHandler(
                        item,
                        job?.topicInfo?.uniqueTopicId || item.wantableTopics?.[0]?.uniqueTopicId
                      )
                    "
                    :class="['result-checkbox', { checked: checkedList.has(item.url) }]"
                  ></a>
                  <AppImage
                    v-if="curTab === 'image'"
                    :src="item.imageResize512Url || item.imageResize200Url"
                    :fullscreen="item.url"
                    :alt="item.prompt"
                  />
                  <div class="video-box" v-if="curTab === 'video'">
                    <VideoPlayer :itemData="item" :showVideoTag="false" :isShowControls="false" />
                    <div class="duration">{{ formatDuration(item?.videoExtraInfo.duration) }}</div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
      <div v-else class="empty">
        <img src="@/assets/images/empty.png" alt="Buy Now" />
        <p class="text">
          尽情发挥奇思妙想，快去创作
          <br />
          属于自己的作品吧～
        </p>
      </div>
      <div class="loading-wraper-two" v-if="loading"><AppLoading /></div>
    </div>
  </div>
</template>
<style lang="stylus" scoped>
  @import './GeneratorHistory.styl';
</style>
